import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Plus, Settings, MessageSquare, Mail, Phone, CheckCircle2, XCircle, Send, Loader2, CreditCard, Building, AlertTriangle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IntegrationsPage = () => {
  const { getAuthHeader } = useAuth();
  const [integrations, setIntegrations] = useState([]);
  const [availableProviders, setAvailableProviders] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('payment');
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [formData, setFormData] = useState({
    provider_type: 'sms',
    provider_name: '',
    api_key: '',
    api_secret: '',
    config: {}
  });
  const [testData, setTestData] = useState({
    recipients: '',
    content: '',
    subject: ''
  });
  
  // Payment Gateway State
  const [gatewaySettings, setGatewaySettings] = useState(null);
  const [gatewayForm, setGatewayForm] = useState({
    gateway: 'razorpay',
    key_id: '',
    key_secret: '',
    merchant_name: '',
    use_global: false
  });
  const [gatewayDialogOpen, setGatewayDialogOpen] = useState(false);

  useEffect(() => {
    fetchIntegrations();
    fetchAvailableProviders();
    fetchGatewaySettings();
  }, []);

  const fetchGatewaySettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/payments/gateway-settings`, {
        headers: getAuthHeader()
      });
      setGatewaySettings(response.data);
    } catch (error) {
      console.error('Failed to fetch gateway settings:', error);
    }
  };

  const saveGatewaySettings = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/payments/gateway-settings`, gatewayForm, {
        headers: getAuthHeader()
      });
      toast.success('Payment gateway configured successfully!');
      setGatewayDialogOpen(false);
      fetchGatewaySettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to configure gateway');
    } finally {
      setLoading(false);
    }
  };

  const useGlobalGateway = async () => {
    setLoading(true);
    try {
      await axios.delete(`${API_URL}/api/payments/gateway-settings`, {
        headers: getAuthHeader()
      });
      toast.success('Switched to global payment gateway');
      fetchGatewaySettings();
    } catch (error) {
      toast.error('Failed to switch gateway');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIntegrations();
    fetchAvailableProviders();
  }, []);

  const fetchIntegrations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/integrations`, {
        headers: getAuthHeader()
      });
      setIntegrations(response.data);
    } catch (error) {
      console.error('Failed to fetch integrations:', error);
    }
  };

  const fetchAvailableProviders = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/notification-providers/available`);
      setAvailableProviders(response.data);
    } catch (error) {
      console.error('Failed to fetch available providers:', error);
    }
  };

  const handleProviderSelect = (providerName, providerInfo) => {
    setSelectedProvider({ name: providerName, ...providerInfo });
    setFormData({
      ...formData,
      provider_name: providerName,
      config: providerInfo.fields.reduce((acc, field) => ({ ...acc, [field]: '' }), {})
    });
  };

  const handleConfigChange = (field, value) => {
    setFormData({
      ...formData,
      config: { ...formData.config, [field]: value }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // For providers that need specific field in api_key
      const submitData = {
        provider_type: activeTab,
        provider_name: formData.provider_name,
        api_key: formData.config.api_key || formData.config.account_sid || Object.values(formData.config)[0] || '',
        api_secret: formData.config.auth_token || formData.config.api_secret || '',
        config: formData.config
      };

      await axios.post(`${API_URL}/api/integrations`, submitData, {
        headers: getAuthHeader()
      });
      toast.success('Integration added successfully!');
      setDialogOpen(false);
      setSelectedProvider(null);
      setFormData({ provider_type: 'sms', provider_name: '', api_key: '', api_secret: '', config: {} });
      fetchIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add integration');
    } finally {
      setLoading(false);
    }
  };

  const handleTestIntegration = async (integration) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/integrations/test/${integration.id}`,
        {},
        { headers: getAuthHeader() }
      );
      if (response.data.success) {
        toast.success('Provider test passed!');
      } else {
        toast.error(`Test failed: ${response.data.message}`);
      }
      fetchIntegrations();
    } catch (error) {
      toast.error('Test failed: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSendTestNotification = async () => {
    setLoading(true);
    try {
      const recipients = testData.recipients.split('\n').map(r => r.trim()).filter(r => r);
      const response = await axios.post(
        `${API_URL}/api/notifications/send`,
        {
          recipients,
          content: testData.content,
          provider_type: activeTab,
          subject: testData.subject || undefined
        },
        { headers: getAuthHeader() }
      );
      if (response.data.success) {
        toast.success('Test notification sent!');
        setTestDialogOpen(false);
      } else {
        toast.error(`Send failed: ${response.data.error}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send notification');
    } finally {
      setLoading(false);
    }
  };

  const typeIcons = {
    sms: Phone,
    whatsapp: MessageSquare,
    email: Mail
  };

  const typeColors = {
    sms: 'bg-blue-500',
    whatsapp: 'bg-green-500',
    email: 'bg-purple-500'
  };

  return (
    <DashboardLayout title="Integrations">
      <div className="space-y-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="flex items-center justify-between mb-4">
            <TabsList>
              <TabsTrigger value="payment" className="gap-2">
                <CreditCard className="h-4 w-4" />
                Payment Gateway
              </TabsTrigger>
              <TabsTrigger value="sms" className="gap-2">
                <Phone className="h-4 w-4" />
                SMS
              </TabsTrigger>
              <TabsTrigger value="whatsapp" className="gap-2">
                <MessageSquare className="h-4 w-4" />
                WhatsApp
              </TabsTrigger>
              <TabsTrigger value="email" className="gap-2">
                <Mail className="h-4 w-4" />
                Email
              </TabsTrigger>
            </TabsList>
            {activeTab !== 'payment' && (
              <div className="flex gap-2">
                <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" data-testid="test-send-btn">
                      <Send className="h-4 w-4 mr-2" />
                      Test Send
                    </Button>
                  </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Send Test Notification ({activeTab.toUpperCase()})</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Recipients (one per line)</Label>
                      <textarea
                        className="w-full min-h-[80px] px-3 py-2 border rounded-md"
                        placeholder={activeTab === 'email' ? 'email@example.com' : '+919876543210'}
                        value={testData.recipients}
                        onChange={(e) => setTestData({ ...testData, recipients: e.target.value })}
                      />
                    </div>
                    {activeTab === 'email' && (
                      <div className="space-y-2">
                        <Label>Subject</Label>
                        <Input
                          value={testData.subject}
                          onChange={(e) => setTestData({ ...testData, subject: e.target.value })}
                          placeholder="Email subject"
                        />
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>Message</Label>
                      <textarea
                        className="w-full min-h-[100px] px-3 py-2 border rounded-md"
                        placeholder="Your message here..."
                        value={testData.content}
                        onChange={(e) => setTestData({ ...testData, content: e.target.value })}
                      />
                    </div>
                    <Button onClick={handleSendTestNotification} disabled={loading} className="w-full">
                      {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                      Send Test
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-integration-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Provider
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Configure {activeTab.toUpperCase()} Provider</DialogTitle>
                  </DialogHeader>
                  
                  {!selectedProvider ? (
                    <div className="grid grid-cols-2 gap-3">
                      {availableProviders[activeTab] && Object.entries(availableProviders[activeTab]).map(([key, info]) => (
                        <Card
                          key={key}
                          className="cursor-pointer hover:border-primary transition-colors"
                          onClick={() => handleProviderSelect(key, info)}
                          data-testid={`provider-select-${key}`}
                        >
                          <CardContent className="p-4">
                            <p className="font-medium">{info.name}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {info.fields.length} config fields
                            </p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                        <div className={`${typeColors[activeTab]} p-2 rounded`}>
                          {activeTab === 'sms' && <Phone className="h-4 w-4 text-white" />}
                          {activeTab === 'whatsapp' && <MessageSquare className="h-4 w-4 text-white" />}
                          {activeTab === 'email' && <Mail className="h-4 w-4 text-white" />}
                        </div>
                        <div>
                          <p className="font-medium">{selectedProvider.name}</p>
                          <p className="text-xs text-muted-foreground">{activeTab.toUpperCase()} Provider</p>
                        </div>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm" 
                          className="ml-auto"
                          onClick={() => setSelectedProvider(null)}
                        >
                          Change
                        </Button>
                      </div>

                      <div className="space-y-3">
                        {selectedProvider.fields.map((field) => (
                          <div key={field} className="space-y-1">
                            <Label htmlFor={field} className="capitalize">
                              {field.replace(/_/g, ' ')} *
                            </Label>
                            <Input
                              id={field}
                              type={field.includes('key') || field.includes('secret') || field.includes('token') ? 'password' : 'text'}
                              value={formData.config[field] || ''}
                              onChange={(e) => handleConfigChange(field, e.target.value)}
                              placeholder={`Enter ${field.replace(/_/g, ' ')}`}
                              required
                              data-testid={`config-${field}`}
                            />
                          </div>
                        ))}
                      </div>

                      <div className="flex justify-end gap-2 pt-4">
                        <Button type="button" variant="outline" onClick={() => {
                          setSelectedProvider(null);
                          setDialogOpen(false);
                        }}>
                          Cancel
                        </Button>
                        <Button type="submit" disabled={loading} data-testid="submit-provider-btn">
                          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                          Save Provider
                        </Button>
                      </div>
                    </form>
                  )}
                </DialogContent>
              </Dialog>
            </div>
            )}
          </div>

          {/* Payment Gateway Tab */}
          <TabsContent value="payment">
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="bg-green-500 p-3 rounded-lg">
                        <CreditCard className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <CardTitle>Payment Gateway</CardTitle>
                        <CardDescription>Configure Razorpay for online fee collection</CardDescription>
                      </div>
                    </div>
                    {gatewaySettings?.is_configured && (
                      <Badge className="bg-green-500">
                        <CheckCircle2 className="h-3 w-3 mr-1" /> Active
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {gatewaySettings ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="p-4 bg-muted/50 rounded-lg">
                          <p className="text-sm text-muted-foreground">Gateway</p>
                          <p className="font-semibold capitalize">{gatewaySettings.gateway || 'Razorpay'}</p>
                        </div>
                        <div className="p-4 bg-muted/50 rounded-lg">
                          <p className="text-sm text-muted-foreground">Status</p>
                          <p className={`font-semibold ${gatewaySettings.is_configured ? 'text-green-600' : 'text-red-600'}`}>
                            {gatewaySettings.is_configured ? 'Configured' : 'Not Configured'}
                          </p>
                        </div>
                        <div className="p-4 bg-muted/50 rounded-lg">
                          <p className="text-sm text-muted-foreground">Key Type</p>
                          <p className="font-semibold">
                            {gatewaySettings.use_school_keys ? 'School Keys' : 'Global Keys'}
                          </p>
                        </div>
                        <div className="p-4 bg-muted/50 rounded-lg">
                          <p className="text-sm text-muted-foreground">Key ID</p>
                          <p className="font-semibold font-mono text-sm">{gatewaySettings.key_id || 'Not set'}</p>
                        </div>
                      </div>

                      {!gatewaySettings.is_configured && (
                        <div className="flex items-center gap-3 p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
                          <AlertTriangle className="h-5 w-5 text-amber-600" />
                          <div>
                            <p className="font-medium text-amber-800 dark:text-amber-200">Payment Gateway Not Configured</p>
                            <p className="text-sm text-amber-700 dark:text-amber-300">Online fee collection is disabled. Configure your Razorpay keys to enable payments.</p>
                          </div>
                        </div>
                      )}

                      <div className="flex gap-3">
                        <Dialog open={gatewayDialogOpen} onOpenChange={setGatewayDialogOpen}>
                          <DialogTrigger asChild>
                            <Button>
                              <Settings className="h-4 w-4 mr-2" />
                              {gatewaySettings.use_school_keys ? 'Update Keys' : 'Add Your Keys'}
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Configure Razorpay Payment Gateway</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4">
                              <div className="p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                                <p className="text-sm text-blue-800 dark:text-blue-200">
                                  <strong>How to get Razorpay API Keys:</strong>
                                </p>
                                <ol className="text-sm text-blue-700 dark:text-blue-300 list-decimal ml-4 mt-2 space-y-1">
                                  <li>Go to <a href="https://dashboard.razorpay.com" target="_blank" rel="noopener noreferrer" className="underline">Razorpay Dashboard</a></li>
                                  <li>Navigate to Settings → API Keys</li>
                                  <li>Generate new keys or use existing ones</li>
                                  <li>Copy Key ID and Key Secret</li>
                                </ol>
                              </div>
                              
                              <div className="space-y-2">
                                <Label>Merchant/School Name</Label>
                                <Input
                                  placeholder="Your School Name"
                                  value={gatewayForm.merchant_name}
                                  onChange={(e) => setGatewayForm({...gatewayForm, merchant_name: e.target.value})}
                                />
                              </div>
                              
                              <div className="space-y-2">
                                <Label>Razorpay Key ID *</Label>
                                <Input
                                  placeholder="rzp_live_xxxxxxxxxx"
                                  value={gatewayForm.key_id}
                                  onChange={(e) => setGatewayForm({...gatewayForm, key_id: e.target.value})}
                                />
                              </div>
                              
                              <div className="space-y-2">
                                <Label>Razorpay Key Secret *</Label>
                                <Input
                                  type="password"
                                  placeholder="Enter your secret key"
                                  value={gatewayForm.key_secret}
                                  onChange={(e) => setGatewayForm({...gatewayForm, key_secret: e.target.value})}
                                />
                              </div>

                              <Button onClick={saveGatewaySettings} disabled={loading} className="w-full">
                                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                                Save & Verify Keys
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>

                        {gatewaySettings.use_school_keys && gatewaySettings.global_available && (
                          <Button variant="outline" onClick={useGlobalGateway} disabled={loading}>
                            Use Global Gateway
                          </Button>
                        )}

                        <a href="https://dashboard.razorpay.com" target="_blank" rel="noopener noreferrer">
                          <Button variant="outline">
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Razorpay Dashboard
                          </Button>
                        </a>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center p-8">
                      <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Payment Gateway Info Cards */}
              <div className="grid md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg">
                        <Building className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium">For Schools</p>
                        <p className="text-sm text-muted-foreground">Parents can pay fees online via UPI, Cards, Net Banking</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="bg-green-100 dark:bg-green-900/30 p-2 rounded-lg">
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium">Automatic Receipts</p>
                        <p className="text-sm text-muted-foreground">Payment receipts are generated automatically</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="bg-purple-100 dark:bg-purple-900/30 p-2 rounded-lg">
                        <CreditCard className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium">Secure Payments</p>
                        <p className="text-sm text-muted-foreground">PCI DSS compliant, bank-grade security</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {['sms', 'whatsapp', 'email'].map((type) => {
            const Icon = typeIcons[type];
            const typeIntegrations = integrations.filter(i => i.provider_type === type);
            
            return (
              <TabsContent key={type} value={type}>
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <div className={`${typeColors[type]} p-2 rounded-lg`}>
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <CardTitle className="capitalize">{type} Providers</CardTitle>
                        <CardDescription>
                          {typeIntegrations.length} provider(s) configured
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {typeIntegrations.length === 0 ? (
                      <div className="text-center py-8">
                        <Icon className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                        <p className="text-muted-foreground">No {type} providers configured yet</p>
                        <Button 
                          variant="outline" 
                          className="mt-4"
                          onClick={() => {
                            setActiveTab(type);
                            setDialogOpen(true);
                          }}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add {type.toUpperCase()} Provider
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {typeIntegrations.map(integration => (
                          <div
                            key={integration.id}
                            data-testid={`integration-${integration.id}`}
                            className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <div className={`${typeColors[type]} p-2 rounded`}>
                                <Icon className="h-4 w-4 text-white" />
                              </div>
                              <div>
                                <p className="font-medium">{integration.provider_name}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  {integration.is_active ? (
                                    <Badge variant="success" className="bg-green-100 text-green-800 text-xs">Active</Badge>
                                  ) : (
                                    <Badge variant="secondary" className="text-xs">Inactive</Badge>
                                  )}
                                  {integration.test_status === 'success' && (
                                    <span className="flex items-center gap-1 text-xs text-green-600">
                                      <CheckCircle2 className="h-3 w-3" />
                                      Verified
                                    </span>
                                  )}
                                  {integration.test_status === 'failed' && (
                                    <span className="flex items-center gap-1 text-xs text-red-600">
                                      <XCircle className="h-3 w-3" />
                                      Failed
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleTestIntegration(integration)}
                                disabled={loading}
                              >
                                Test
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Available Providers */}
                <Card className="mt-4">
                  <CardHeader>
                    <CardTitle className="text-base">Available {type.toUpperCase()} Providers</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {availableProviders[type] && Object.entries(availableProviders[type]).map(([key, info]) => {
                        const isConfigured = typeIntegrations.some(i => i.provider_name === key);
                        return (
                          <div
                            key={key}
                            className={`p-3 border rounded-lg text-center ${isConfigured ? 'bg-green-50 border-green-200' : 'bg-muted/30'}`}
                          >
                            <p className="font-medium text-sm">{info.name}</p>
                            {isConfigured ? (
                              <Badge variant="success" className="mt-1 bg-green-100 text-green-800 text-xs">Configured</Badge>
                            ) : (
                              <p className="text-xs text-muted-foreground mt-1">Not configured</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            );
          })}
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default IntegrationsPage;
